import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import alertApi from '../services/alertApi';

// Stable empty array reference to avoid infinite re-renders in consumers
// that do `data?.alerts || []` (see useBookings.js for the precedent).
const EMPTY_ALERTS = [];

// Shared queryKey so AdminDashboard's count card and /alerts page stay in sync
// when a resolve-mutation invalidates it.
export const ALERTS_QUERY_KEY = (resolved) => ['alerts', { resolved }];

export const useUnresolvedAlerts = () => {
  const result = useQuery({
    queryKey: ALERTS_QUERY_KEY(false),
    queryFn: () => alertApi.getAlerts({ resolved: false })
  });

  return {
    ...result,
    alerts: result.data?.alerts ?? EMPTY_ALERTS
  };
};

export const useAllAlerts = () => {
  const result = useQuery({
    queryKey: ALERTS_QUERY_KEY('all'),
    queryFn: () => alertApi.getAlerts()
  });

  return {
    ...result,
    alerts: result.data?.alerts ?? EMPTY_ALERTS
  };
};

// Invalidates every `['alerts', ...]` cache entry so both resolved/unresolved
// views + the dashboard count card refetch.
export const useResolveAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (alertId) => alertApi.resolveAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
    onError: (error) => {
      console.error('Failed to resolve alert:', error);
    }
  });
};

// Claim/release (Phase 2 ownership). 409 = someone else claimed first / not
// owner — invalidate on error too so the UI refreshes to show the real owner.
export const useClaimAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (alertId) => alertApi.claimAlert(alertId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    }
  });
};

export const useReleaseAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (alertId) => alertApi.releaseAlert(alertId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    }
  });
};
